import React, { useMemo, useEffect, useRef } from "react";
import { debounce } from "lodash";
import { Input } from "../../components/common";
import { Box, InputAdornment, Typography } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import JobCards from "./JobCards";
import CardLoading from "./CardLoading";
import NotFoundPage from "../../components/common/NotFoundPage";

interface Job {
  id: string | number;
  title: string;
  company: string;
  [key: string]: any;
}

interface JobListingsProps {
  jobPostings: Job[];
  token: string | null;
  loading: boolean;
  /** Job cards are still waiting. The search header stays visible. */
  cardsPending?: boolean;
  pagination: any;
  onLoadMore: () => void;
  setIsSavedChanged: React.Dispatch<React.SetStateAction<boolean>>;
  /** Controlled search; parent debounces and passes `q` to the careers API. */
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
}

const JobListings: React.FC<JobListingsProps> = ({
  jobPostings,
  token,
  loading,
  cardsPending = false,
  pagination,
  onLoadMore,
  setIsSavedChanged,
  searchTerm,
  onSearchTermChange,
}) => {
  const loadingMoreGuard = useRef(false);

  useEffect(() => {
    if (!loading) loadingMoreGuard.current = false;
  }, [loading]);

  const currentPage =
    pagination?.current ?? pagination?.page ?? 1;
  const totalPages = pagination?.total ?? 0;

  const debouncedScrollLoad = useMemo(
    () =>
      debounce(() => {
        const { bottom } = document.documentElement.getBoundingClientRect();
        const nearBottom = bottom <= window.innerHeight + 400;
        if (
          !nearBottom ||
          loading ||
          loadingMoreGuard.current ||
          totalPages < 1 ||
          currentPage >= totalPages
        ) {
          return;
        }
        loadingMoreGuard.current = true;
        onLoadMore();
      }, 120),
    [loading, currentPage, totalPages, onLoadMore]
  );

  useEffect(() => {
    const handleScroll = () => debouncedScrollLoad();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      debouncedScrollLoad.cancel();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [debouncedScrollLoad]);

  return (
    <Box
      className={`job-listing-wrapper filter-open ${
        !token ? "no-token" : ""
      }`}
    >
      <Box className="job-listing-search">
        <Typography variant="h4">Find your dream job here!</Typography>
        <Typography variant="body1">
          Explore the latest job openings and apply for the best job
          opportunities available today!
        </Typography>
        <Input
          type="text"
          className="job-location-search"
          placeholder="Search for job title"
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onSearchTermChange(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            "aria-label": "Search by job title",
          }}
        />
      </Box>
      <Box className="jobs-card-wrapper">
        {cardsPending ? (
          [1, 2, 3, 4, 5, 6].map((key) => <CardLoading key={key} variant="card" />)
        ) : jobPostings.length > 0 ? (
          jobPostings.map((job) => (
            <JobCards
              key={job.id}
              job={job}
              setIsSavedChanged={setIsSavedChanged}
            />
          ))
        ) : !loading ? (
          <NotFoundPage variant="searched-jobs" />
        ) : null}
      </Box>
    </Box>
  );
};

export default JobListings;
